import { useMemo, useState } from "react"
import {
  AlertCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  Mail,
  X,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { formatFullDate } from "@/lib/format"
import {
  useApproveApplication,
  useCreatorApplications,
  useRejectApplication,
  type CreatorApplication,
  type CreatorApplicationStatus,
} from "@/lib/queries/creatorApplications"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Creator Applications (V-L Phase 2)
// API:   POST /admin/creator_application_list
//        POST /admin/creator_application_approve  { id }
//        POST /admin/creator_application_reject   { id, reason? }
//
// Inbox-style table with filter pills (Pending / Approved / Rejected /
// All). Click a row → side panel with full bio + sample links + actions.
// Approve creates the Coach record + sets user.coach_id + notifies the
// applicant. Reject takes an optional reason that's surfaced back to
// the user via notification.
// ───────────────────────────────────────────────────────────────────────

const STATUSES: { key: "pending" | "approved" | "rejected" | "all"; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
]

function StatusBadge({ status }: { status: CreatorApplicationStatus }) {
  const tone =
    status === "approved"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : status === "rejected"
        ? "bg-rose-50 text-rose-700 border-rose-200"
        : "bg-amber-50 text-amber-700 border-amber-200"
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize",
        tone
      )}
    >
      {status}
    </span>
  )
}

export default function CreatorApplications() {
  const [filter, setFilter] = useState<
    "pending" | "approved" | "rejected" | "all"
  >("pending")
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [open, setOpen] = useState<CreatorApplication | null>(null)
  const [rejectingFor, setRejectingFor] = useState<CreatorApplication | null>(
    null
  )
  const [rejectReason, setRejectReason] = useState("")

  const params = useMemo(
    () => ({
      status: filter === "all" ? undefined : filter,
      page,
      limit,
    }),
    [filter, page, limit]
  )

  const q = useCreatorApplications(params)
  const approve = useApproveApplication()
  const reject = useRejectApplication()

  const docs = q.data?.docs ?? []
  const totalDocs = q.data?.totalDocs ?? 0
  const totalPages = q.data?.totalPages ?? 0

  const onApprove = async (id: string) => {
    try {
      await approve.mutateAsync(id)
      setOpen(null)
    } catch (e) {
      // Mutation hook surfaces error state; nothing extra to do.
      console.error("approve failed:", e)
    }
  }

  const onReject = async () => {
    if (!rejectingFor) return
    try {
      await reject.mutateAsync({
        id: rejectingFor.id,
        reason: rejectReason.trim() || undefined,
      })
      setRejectingFor(null)
      setRejectReason("")
      setOpen(null)
    } catch (e) {
      console.error("reject failed:", e)
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Creator Applications</CardTitle>
              <CardDescription>
                {totalDocs} {totalDocs === 1 ? "application" : "applications"}
                {filter !== "all" && ` (${filter})`}
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-1">
              {STATUSES.map((s) => (
                <Button
                  key={s.key}
                  size="sm"
                  variant={filter === s.key ? "default" : "outline"}
                  onClick={() => {
                    setFilter(s.key)
                    setPage(1)
                  }}
                >
                  {s.label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {q.isError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load applications. Try refreshing.</span>
            </div>
          )}

          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Bio</TableHead>
                  <TableHead>Topics</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10">
                      <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : docs.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-10 text-muted-foreground"
                    >
                      No applications match the current filter.
                    </TableCell>
                  </TableRow>
                ) : (
                  docs.map((a) => (
                    <TableRow
                      key={a.id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => setOpen(a)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {a.applicant?.avatarUrl ? (
                            <img
                              src={a.applicant.avatarUrl}
                              alt=""
                              className="h-8 w-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="grid h-8 w-8 place-items-center rounded-full bg-muted text-xs font-medium">
                              {(a.applicant?.name || "?")
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="font-medium">
                              {a.applicant?.name || "Unknown"}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              {a.applicant?.email || "—"}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="line-clamp-2 text-sm text-muted-foreground">
                          {a.bio || (
                            <span className="italic">No bio provided</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(a.topics || []).slice(0, 3).map((t) => (
                            <span
                              key={t}
                              className="inline-flex items-center rounded-full border bg-muted/40 px-2 py-0.5 text-xs"
                            >
                              {t}
                            </span>
                          ))}
                          {(a.topics || []).length > 3 && (
                            <span className="text-xs text-muted-foreground">
                              +{(a.topics || []).length - 3}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {a.submittedAt ? formatFullDate(a.submittedAt) : "—"}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={a.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div
                          className="flex justify-end gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {a.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                variant="default"
                                disabled={approve.isPending}
                                onClick={() => onApprove(a.id)}
                              >
                                <Check className="mr-1 h-4 w-4" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setRejectingFor(a)}
                              >
                                <X className="mr-1 h-4 w-4" />
                                Reject
                              </Button>
                            </>
                          )}
                          {a.status !== "pending" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setOpen(a)}
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-lg">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle>{open.applicant?.name || "Applicant"}</DialogTitle>
                <DialogDescription>
                  {open.applicant?.email} · Submitted{" "}
                  {open.submittedAt ? formatFullDate(open.submittedAt) : "—"}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <StatusBadge status={open.status} />
                  {open.coachId && (
                    <span className="text-xs text-muted-foreground">
                      Linked coach: {open.coachId.slice(-6)}
                    </span>
                  )}
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Bio
                  </Label>
                  <p className="mt-1 text-sm whitespace-pre-wrap">
                    {open.bio || <em className="text-muted-foreground">None</em>}
                  </p>
                </div>

                {(open.topics || []).length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Topics
                    </Label>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {open.topics.map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center rounded-full border bg-muted/40 px-2 py-0.5 text-xs"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(open.sampleLinks || []).length > 0 && (
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Sample links
                    </Label>
                    <ul className="mt-1 space-y-1">
                      {open.sampleLinks.map((l) => (
                        <li key={l}>
                          <a
                            href={l}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-primary underline"
                          >
                            <ExternalLink className="h-3 w-3" />
                            {l}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {open.status === "rejected" && open.rejectionReason && (
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                      Rejection reason
                    </Label>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {open.rejectionReason}
                    </p>
                  </div>
                )}
              </div>

              <DialogFooter>
                {open.status === "pending" ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setRejectingFor(open)}
                    >
                      <X className="mr-1 h-4 w-4" />
                      Reject
                    </Button>
                    <Button
                      disabled={approve.isPending}
                      onClick={() => onApprove(open.id)}
                    >
                      {approve.isPending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      <Check className="mr-1 h-4 w-4" />
                      Approve
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" onClick={() => setOpen(null)}>
                    Close
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject reason dialog */}
      <Dialog
        open={!!rejectingFor}
        onOpenChange={(o) => {
          if (!o) {
            setRejectingFor(null)
            setRejectReason("")
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject application</DialogTitle>
            <DialogDescription>
              Optionally provide a reason — it will be sent to the applicant
              via notification so they understand the decision.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Reason (optional)</Label>
            <Textarea
              id="reject-reason"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Bio is too short — please add more about your background and what content you'd like to create."
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectingFor(null)
                setRejectReason("")
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={reject.isPending}
              onClick={onReject}
            >
              {reject.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Reject application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
